/**
 * Strava API-tjenester
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';

const STRAVA_CLIENT_ID = import.meta.env.VITE_STRAVA_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_STRAVA_REDIRECT_URI;

const STRAVA_AUTH_URL = 'https://www.strava.com/oauth/authorize';
const STRAVA_API_URL = 'https://www.strava.com/api/v3';

// Initialize Cloud Functions
const functions = getFunctions(app);

/**
 * Initier Strava OAuth-flow
 */
export const initiateStravaAuth = () => {
  const params = new URLSearchParams({
    client_id: STRAVA_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    approval_prompt: 'force', // Force re-approval to ensure all scopes are granted
    scope: 'activity:read_all,profile:read_all'
  });

  window.location.href = `${STRAVA_AUTH_URL}?${params.toString()}`;
};

/**
 * Bytt authorization code mot access token (via Cloud Function)
 * SIKKERHET: Client Secret holdes på server-side
 */
export const exchangeToken = async (code) => {
  try {
    const exchangeStravaToken = httpsCallable(functions, 'exchangeStravaToken');
    const result = await exchangeStravaToken({ code });
    return result.data;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw new Error('Failed to exchange token: ' + error.message);
  }
};

/**
 * Refresh access token (via Cloud Function)
 * SIKKERHET: Client Secret holdes på server-side
 */
export const refreshAccessToken = async (refreshToken) => {
  try {
    const refreshStravaToken = httpsCallable(functions, 'refreshStravaToken');
    const result = await refreshStravaToken({ refreshToken });
    return result.data;
  } catch (error) {
    console.error('Token refresh error:', error);
    throw new Error('Failed to refresh token: ' + error.message);
  }
};

/**
 * Hent brukerens aktiviteter fra Strava
 */
export const getActivities = async (accessToken, after = null, perPage = 30) => {
  const params = new URLSearchParams({
    per_page: perPage.toString()
  });

  if (after) {
    params.append('after', Math.floor(after.getTime() / 1000).toString());
  }

  const response = await fetch(`${STRAVA_API_URL}/athlete/activities?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch activities');
  }

  return await response.json();
};

/**
 * Hent brukerens profil fra Strava
 */
export const getAthlete = async (accessToken) => {
  const response = await fetch(`${STRAVA_API_URL}/athlete`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch athlete');
  }

  return await response.json();
};

/**
 * Deauthorize Strava-tilgang
 */
export const deauthorizeStrava = async (accessToken) => {
  const response = await fetch('https://www.strava.com/oauth/deauthorize', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to deauthorize');
  }

  return await response.json();
};

/**
 * Sjekk om access token er utløpt
 */
export const isTokenExpired = (expiresAt) => {
  return Date.now() / 1000 >= expiresAt;
};
